/**
 * Kalender mit Kalenderwochen und die Outlook-Termine (v11, 5.2 und 5.3).
 *
 * Beide Karten hängen an derselben Auswahl und an denselben Daten, deshalb
 * stehen sie in einem Bauteil: Der Kalender setzt die Auswahl, die
 * Terminkarte zeigt sie. Ein getrenntes Paar müsste den Zustand über die
 * Seite reichen und würde denselben Monat zweimal laden.
 *
 * Gelesen wird ausschließlich – KORA legt keine Termine an. Fehlt die
 * Berechtigung im Entra-Portal oder ist Graph im Testbetrieb abgeschaltet,
 * sagt die Karte das im Klartext, statt leer zu bleiben.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { useMeta } from '../zustand';
import type { Kalendertermin, TermineAntwort } from '../typen';
import './terminbereich.css';

/**
 * Nur drei Kategorien, nur drei Farben (v11, 5.3).
 *
 * Die Hex-Werte stehen nicht hier, sondern in der Palette vom Server: Die
 * Kategorien tragen dieselben Materialtöne wie die Rubriken, und eine
 * zweite Liste derselben Farben läuft irgendwann auseinander.
 */
const ART_TON: Record<Kalendertermin['art'], string> = {
  vorOrt: 'tiefbronze',
  telefon: 'stone',
  intern: 'taupe',
};

const ART_NAME: Record<Kalendertermin['art'], string> = {
  vorOrt: 'Vor Ort',
  telefon: 'Telefon',
  intern: 'Intern',
};

const MONATE = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
];
const WTAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const KURZ = ['Jän.', 'Feb.', 'März', 'Apr.', 'Mai', 'Juni', 'Juli', 'Aug.', 'Sep.', 'Okt.', 'Nov.', 'Dez.'];
const KURZTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const ausIso = (s: string) => new Date(+s.slice(0, 4), +s.slice(5, 7) - 1, +s.slice(8, 10));
const kurzTag = (s: string) => {
  const d = ausIso(s);
  return `${KURZTAG[d.getDay()]} ${d.getDate()}. ${KURZ[d.getMonth()]}`;
};

/**
 * ISO-Kalenderwoche über den Donnerstag derselben Woche.
 *
 * Der Donnerstag entscheidet, zu welchem Jahr eine Woche gehört – deshalb
 * wird er gesucht und nicht der Montag gezählt. Sonst bekommt der 1. Jänner
 * je nach Wochentag die KW 1 oder die KW 53 des Vorjahres falsch zugeordnet.
 */
function kwVon(d: Date): number {
  const do_ = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const jan1 = new Date(do_.getFullYear(), 0, 1);
  return Math.ceil(((do_.getTime() - jan1.getTime()) / 86_400_000 + 1) / 7);
}

const minuten = (t: string) => +t.slice(0, 2) * 60 + +t.slice(3, 5);

type Wahl = { typ: 'tag' | 'woche'; d: string };
type Termin = TermineAntwort['termine'][number];

export function Terminbereich({ person, heute }: { person: string; heute: string }) {
  const navigate = useNavigate();
  const meta = useMeta();
  /** Farbe einer Kategorie aus der Palette; unbekannt fällt auf Taupe zurück. */
  const artFarbe = (art: Kalendertermin['art']) =>
    meta.toene.find((t) => t.id === ART_TON[art])?.akz ?? 'var(--eh-text-3)';
  const start = ausIso(heute);
  const [jahr, setJahr] = useState(start.getFullYear());
  const [monat, setMonat] = useState(start.getMonth());
  const [wahl, setWahl] = useState<Wahl>({ typ: 'tag', d: heute });
  const [offen, setOffen] = useState<string | null>(null);

  /* Jede Minute genügt: „in 40 Min." darf nicht stehen bleiben. */
  const [, tick] = useState(0);
  useEffect(() => {
    const t = window.setInterval(() => tick((n) => n + 1), 60_000);
    return () => window.clearInterval(t);
  }, []);

  /*
   * Geladen wird das ganze sichtbare Blatt samt Rändern, nicht der gewählte
   * Tag: Die Punkte unter den Zahlen brauchen den Monat, und ein Aufruf je
   * Tagesklick wäre Verkehr ohne Gewinn.
   */
  const erster = new Date(jahr, monat, 1);
  const blattVon = new Date(jahr, monat, 1 - ((erster.getDay() + 6) % 7));
  const blattBis = new Date(blattVon.getFullYear(), blattVon.getMonth(), blattVon.getDate() + 41);
  const vonIso = iso(blattVon);
  const bisIso = iso(blattBis);

  const abfrage = useQuery({
    queryKey: ['termine', person, vonIso, bisIso],
    queryFn: () =>
      api.hole<TermineAntwort>(
        `/termine?von=${vonIso}&bis=${bisIso}${person ? `&person=${encodeURIComponent(person)}` : ''}`,
      ),
    staleTime: 120_000,
    retry: false,
  });

  /** Termine je Tag, jeweils nach Beginn sortiert. */
  const jeTag = useMemo(() => {
    const karte = new Map<string, Termin[]>();
    for (const t of abfrage.data?.termine ?? []) {
      const liste = karte.get(t.tag) ?? [];
      liste.push(t);
      karte.set(t.tag, liste);
    }
    for (const liste of karte.values()) {
      liste.sort((a, b) => (a.ganztags ? -1 : b.ganztags ? 1 : minuten(a.von) - minuten(b.von)));
    }
    return karte;
  }, [abfrage.data]);

  const stand = abfrage.data?.stand ?? 'ok';
  const jetzt = new Date();
  const jetztMin = jetzt.getHours() * 60 + jetzt.getMinutes();

  /* --- Monatsblatt --------------------------------------------------- */
  const wochen: {
    kw: number;
    montag: string;
    tage: { iso: string; tag: number; fremd: boolean; heute: boolean; anzahl: number; punkt: string }[];
  }[] = [];
  for (let w = 0; w < 6; w++) {
    const tage = [];
    let kw = 0;
    for (let k = 0; k < 7; k++) {
      const d = new Date(blattVon.getFullYear(), blattVon.getMonth(), blattVon.getDate() + w * 7 + k);
      if (k === 3) kw = kwVon(d);
      const i = iso(d);
      const liste = jeTag.get(i) ?? [];
      tage.push({
        iso: i,
        tag: d.getDate(),
        fremd: d.getMonth() !== monat,
        heute: i === heute,
        anzahl: liste.length,
        punkt: liste[0] ? artFarbe(liste[0].art) : 'var(--eh-text-4)',
      });
    }
    /* Vollständig fremde Randwochen fallen weg – sechs Zeilen sind selten nötig. */
    if (!tage.some((t) => !t.fremd)) continue;
    wochen.push({ kw, montag: tage[0]!.iso, tage });
  }

  /* --- Auswahl → Tage ------------------------------------------------ */
  const wocheAb = (m: string) => {
    const d0 = ausIso(m);
    return [0, 1, 2, 3, 4, 5, 6].map((k) => iso(new Date(d0.getFullYear(), d0.getMonth(), d0.getDate() + k)));
  };
  const tageDerWahl = wahl.typ === 'woche' ? wocheAb(wahl.d) : [wahl.d];
  const kwWahl = kwVon(ausIso(wahl.d));

  const gruppen = tageDerWahl
    .map((tag) => ({ tag, liste: jeTag.get(tag) ?? [] }))
    .filter((g) => wahl.typ === 'tag' || g.liste.length > 0);
  const anzahl = gruppen.reduce((s, g) => s + g.liste.length, 0);

  const kopfText =
    wahl.typ === 'woche'
      ? `KW ${kwWahl} · ${kurzTag(tageDerWahl[0]!).slice(3)}–${kurzTag(tageDerWahl[6]!).slice(3)}`
      : wahl.d === heute
        ? `heute, ${kurzTag(wahl.d)}`
        : kurzTag(wahl.d);

  /** Der nächste Termin heute – nur einer trägt den Hinweis. */
  const naechster = (() => {
    const liste = jeTag.get(heute) ?? [];
    return liste.find((e) => !e.ganztags && minuten(e.von) > jetztMin) ?? null;
  })();

  return (
    <>
      {/* --- Kalender ------------------------------------------------- */}
      <section className="tb-karte tb-kal">
        <div className="tb-kopf">
          <span className="tb-kopf-text">
            <span className="tb-kicker">
              Kalender · {wahl.typ === 'woche' ? `KW ${kwWahl}` : `KW ${kwWahl} · ${kurzTag(wahl.d)}`}
            </span>
            <span className="tb-monat">
              {MONATE[monat]} {jahr}
            </span>
          </span>
          <span className="tb-blaettern">
            <button
              type="button"
              title="Voriger Monat"
              onClick={() => {
                const d = new Date(jahr, monat - 1, 1);
                setJahr(d.getFullYear());
                setMonat(d.getMonth());
              }}
            >
              ‹
            </button>
            <button
              type="button"
              title="Nächster Monat"
              onClick={() => {
                const d = new Date(jahr, monat + 1, 1);
                setJahr(d.getFullYear());
                setMonat(d.getMonth());
              }}
            >
              ›
            </button>
          </span>
        </div>

        <div className="tb-wtage">
          <span className="tb-kw-kopf">KW</span>
          {WTAGE.map((w, k) => (
            <span className={`tb-wtag ${k > 4 ? 'still' : ''}`} key={w}>
              {w}
            </span>
          ))}
        </div>

        <div className="tb-wochen">
          {wochen.map((w) => {
            const gewaehlt = wahl.typ === 'woche' && wahl.d === w.montag;
            const hatHeute = w.tage.some((t) => t.heute);
            return (
              <div className={`tb-woche ${gewaehlt ? 'gewaehlt' : ''}`} key={w.montag}>
                <button
                  type="button"
                  className={`tb-kw ${gewaehlt ? 'gewaehlt' : ''} ${hatHeute ? 'heute' : ''}`}
                  title={`KW ${w.kw} · ganze Woche ansehen`}
                  onClick={() => setWahl({ typ: 'woche', d: w.montag })}
                >
                  {w.kw}
                </button>
                {w.tage.map((t) => {
                  const tagGewaehlt = wahl.typ === 'tag' && wahl.d === t.iso;
                  return (
                    <button
                      type="button"
                      key={t.iso}
                      className={`tb-tag ${tagGewaehlt ? 'gewaehlt' : ''} ${t.heute ? 'heute' : ''} ${t.fremd ? 'fremd' : ''}`}
                      title={`${kurzTag(t.iso)} · ${t.anzahl === 0 ? 'kein Termin' : `${t.anzahl} ${t.anzahl === 1 ? 'Termin' : 'Termine'}`}`}
                      onClick={() => setWahl({ typ: 'tag', d: t.iso })}
                    >
                      {t.tag}
                      {t.anzahl > 0 && (
                        <span
                          className="tb-punkt"
                          style={{ background: tagGewaehlt ? 'var(--eh-text)' : t.punkt }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="tb-fuss">
          <span className="tb-legende">
            <span className="tb-legende-punkt" />
            Outlook-Termin · KW anklicken zeigt die Woche
          </span>
          <button
            type="button"
            className="tb-heute"
            onClick={() => {
              const d = ausIso(heute);
              setJahr(d.getFullYear());
              setMonat(d.getMonth());
              setWahl({ typ: 'tag', d: heute });
            }}
          >
            Heute
          </button>
        </div>
      </section>

      {/* --- Outlook -------------------------------------------------- */}
      <section className="tb-karte tb-termine">
        <div className="tb-ol-kopf">
          <span className="tb-ol-links">
            <span className="tb-ol-zeichen">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M3 10h18M8 3v4M16 3v4" />
              </svg>
            </span>
            <span className="tb-ol-marke">Outlook</span>
            <span className="tb-ol-zeitraum">{kopfText}</span>
          </span>
          <span className="tb-ol-anzahl">
            {stand !== 'ok' ? '—' : anzahl === 0 ? 'frei' : `${anzahl} ${anzahl === 1 ? 'Termin' : 'Termine'}`}
          </span>
        </div>

        {/*
          Kein leerer Kasten, wenn etwas fehlt: Ein Kalender ohne Termine und
          ein Kalender ohne Berechtigung sehen sonst gleich aus.
        */}
        {stand === 'aus' && (
          <span className="tb-ol-hinweis">Outlook ist in diesem Betrieb abgeschaltet – hier stehen keine Termine.</span>
        )}
        {stand === 'keine-berechtigung' && (
          <span className="tb-ol-hinweis">
            Der Anwendung fehlt die Berechtigung <strong>Calendars.Read</strong>. Sobald sie in der
            App-Registrierung erteilt ist, stehen die Termine hier.
          </span>
        )}
        {stand === 'fehler' && (
          <span className="tb-ol-hinweis">Der Kalender ist gerade nicht erreichbar. {abfrage.data?.meldung}</span>
        )}

        {stand === 'ok' &&
          gruppen.map((g) => (
            <div className="tb-gruppe" key={g.tag}>
              {wahl.typ === 'woche' && (
                <span className="tb-gruppe-tag">
                  {kurzTag(g.tag)}
                  {g.tag === heute ? ' · heute' : ''}
                </span>
              )}
              {g.liste.map((e) => {
                const schluessel = `${e.tag}|${e.von}|${e.id}`;
                const auf = offen === schluessel;
                const laeuft =
                  e.tag === heute && !e.ganztags && jetztMin >= minuten(e.von) && jetztMin < minuten(e.bis);
                const vorbei = e.tag === heute && !e.ganztags && jetztMin >= minuten(e.bis);
                const istNaechster = e.tag === heute && naechster?.id === e.id && naechster.tag === e.tag;
                const dauer = e.ganztags ? 'ganztägig' : dauerText(minuten(e.bis) - minuten(e.von));
                return (
                  <div
                    className={`tb-termin ${auf ? 'offen' : ''} ${laeuft || istNaechster ? 'naechster' : ''} ${vorbei && !laeuft && !auf ? 'vorbei' : ''}`}
                    key={schluessel}
                    style={{ borderLeftColor: artFarbe(e.art) }}
                  >
                    <button
                      type="button"
                      className="tb-termin-knopf"
                      title={`${auf ? 'Zuklappen' : 'Termin öffnen'} · ${e.titel}`}
                      onClick={() => setOffen(auf ? null : schluessel)}
                    >
                      <span className="tb-zeit">
                        <span className="tb-von">{e.ganztags ? '—' : e.von}</span>
                        <span className="tb-bis">{e.ganztags ? 'ganzt.' : e.bis}</span>
                      </span>
                      <span className="tb-mitte">
                        <span className="tb-titel">{e.titel}</span>
                        {e.ort && <span className="tb-ort">{e.ort}</span>}
                        {(laeuft || istNaechster) && (
                          <span className="tb-gleich">
                            {laeuft ? 'läuft gerade' : `als Nächstes · ${inText(minuten(e.von) - jetztMin)}`}
                          </span>
                        )}
                      </span>
                      <span className="tb-pfeil">{auf ? '▾' : '▸'}</span>
                    </button>

                    {auf && (
                      <div className="tb-auf">
                        <span className="tb-kategorie">
                          <span className="tb-kat-punkt" style={{ background: artFarbe(e.art) }} />
                          {ART_NAME[e.art]} · {dauer}
                        </span>
                        {e.wer && <span className="tb-wer">{e.wer}</span>}
                        {e.notiz && <span className="tb-notiz">{e.notiz}</span>}
                        <span className="tb-links">
                          {/*
                            Nur wenn der Server die Nummer wirklich einem
                            Vorgang zuordnen konnte. Eine Nummer im Betreff,
                            zu der es nichts gibt, bleibt Text – ein Link in
                            eine leere Maske wäre schlimmer als keiner.
                          */}
                          {e.angebotId !== null && (
                            <button
                              type="button"
                              className="tb-link"
                              onClick={() => navigate(`/angebot/${e.angebotId}`, { state: { herkunft: 'meintag' } })}
                            >
                              {e.nr} öffnen
                            </button>
                          )}
                          {e.webLink && (
                            <a className="tb-link tb-link-outlook" href={e.webLink} target="_blank" rel="noreferrer">
                              In Outlook öffnen
                            </a>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}

        {stand === 'ok' && anzahl === 0 && (
          <span className="tb-ol-leer">
            {wahl.typ === 'woche'
              ? 'In dieser Woche steht nichts im Outlook-Kalender.'
              : 'Kein Termin an diesem Tag – guter Tag zum Nachfassen.'}
          </span>
        )}
      </section>
    </>
  );
}

function dauerText(min: number): string {
  if (min < 60) return `${min} Min.`;
  const std = min / 60;
  return `${(min % 60 ? std.toFixed(1) : String(std)).replace('.', ',')} Std.`;
}

function inText(min: number): string {
  if (min < 60) return `in ${min} Min.`;
  return `in ${Math.floor(min / 60)} Std. ${min % 60} Min.`;
}
